if (import.meta.env.PROD) {
  import('./instrument')
}

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { WebStorageStateStore } from 'oidc-client-ts'
import type { ReactNode } from 'react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from '@/components/ui/sonner'
import {
  AUTH_PROVIDER,
  OIDC_AUTHORITY,
  OIDC_CLIENT_ID,
  OIDC_POST_LOGOUT_REDIRECT_URI,
  OIDC_REDIRECT_URI,
} from '@/config'
import { ThemeProvider } from '@/theme'
import { router } from './router'
import '@/services/i18n'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 300_000,
    },
  },
})

// In OIDC mode, mount react-oidc-context above the router so the PKCE redirect
// callback is handled at any URL. In password mode this is a no-op passthrough.
function WithAuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_PROVIDER !== 'oidc') return <>{children}</>
  return (
    <OidcAuthProvider
      authority={OIDC_AUTHORITY}
      client_id={OIDC_CLIENT_ID}
      redirect_uri={OIDC_REDIRECT_URI}
      post_logout_redirect_uri={OIDC_POST_LOGOUT_REDIRECT_URI}
      scope="openid profile email"
      // Persist tokens in localStorage so a reloaded tab (and Playwright
      // storageState) restores the session rather than forcing re-login.
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname)
      }}
    >
      {children}
    </OidcAuthProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <WithAuthProvider>
          <RouterProvider router={router} />
        </WithAuthProvider>
        <Toaster />
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>
)
